/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import axios from 'axios';

import { type BotAPIRequestConfig } from './axios';

export interface CreateWebsiteCrawlJobRequest {
  dataset_id: string;
  website_url: string;
  max_depth?: number;
  max_pages?: number;
}

export interface CreateWebsiteCrawlJobResponse {
  job_id: string;
  dataset_id: string;
  website_url: string;
  status: number;
  reused: boolean;
}

export interface GetWebsiteCrawlJobStatusRequest {
  job_id?: string;
  dataset_id?: string;
}

export interface GetWebsiteCrawlJobStatusResponse {
  job_id: string;
  dataset_id: string;
  website_url: string;
  status: number;
  max_depth: number;
  max_pages: number;
  discovered_pages: number;
  success_pages: number;
  failed_pages: number;
  skipped_pages: number;
  last_error: string;
  started_at: number;
  finished_at: number;
  created_at: number;
  updated_at: number;
}

export interface ListWebsiteCrawlPagesRequest {
  job_id: string;
  limit?: number;
  offset?: number;
}

export interface WebsiteCrawlPageItem {
  page_id: string;
  document_id: string;
  source_url: string;
  canonical_url?: string;
  title: string;
  status: number;
  http_status: number;
  depth: number;
  char_count: number;
  word_count: number;
  discovered_links: number;
  image_count: number;
  fail_reason?: string;
  crawled_at: number;
  imported_at: number;
  created_at: number;
  updated_at: number;
}

export interface ListWebsiteCrawlPagesResponse {
  job_id: string;
  items: WebsiteCrawlPageItem[];
  total: number;
  has_more: boolean;
}

const buildHeaders = (config: BotAPIRequestConfig = {}) => ({
  ...config.headers,
  'Agw-Js-Conv': 'str',
});

const websiteAxiosInstance = axios.create();

websiteAxiosInstance.interceptors.request.use(config => {
  const setHeader = (key: string, value: string) => {
    if (typeof config.headers.set === 'function') {
      config.headers.set(key, value);
    } else {
      config.headers[key] = value;
    }
  };
  const getHeader = (key: string) => {
    if (typeof config.headers.get === 'function') {
      return config.headers.get(key);
    }
    return config.headers[key];
  };

  setHeader('x-requested-with', 'XMLHttpRequest');
  if (
    ['post', 'get'].includes(config.method?.toLowerCase() ?? '') &&
    !getHeader('content-type')
  ) {
    setHeader('content-type', 'application/json');
    if (!config.data) {
      config.data = {};
    }
  }

  return config;
});

websiteAxiosInstance.interceptors.response.use(response => response.data);

export const websiteKnowledgeApi = {
  createCrawlJob(
    data: CreateWebsiteCrawlJobRequest,
    config: BotAPIRequestConfig = {},
  ) {
    return websiteAxiosInstance.request<CreateWebsiteCrawlJobResponse>({
      url: '/api/knowledge/website/crawl_job/create',
      method: 'POST',
      data,
      ...config,
      headers: buildHeaders(config),
    });
  },

  getCrawlJobStatus(
    params: GetWebsiteCrawlJobStatusRequest,
    config: BotAPIRequestConfig = {},
  ) {
    return websiteAxiosInstance.request<GetWebsiteCrawlJobStatusResponse>({
      url: '/api/knowledge/website/crawl_job/status',
      method: 'GET',
      params,
      ...config,
      headers: buildHeaders(config),
    });
  },

  listCrawlPages(
    params: ListWebsiteCrawlPagesRequest,
    config: BotAPIRequestConfig = {},
  ) {
    return websiteAxiosInstance.request<ListWebsiteCrawlPagesResponse>({
      url: '/api/knowledge/website/crawl_page/list',
      method: 'GET',
      params,
      ...config,
      headers: buildHeaders(config),
    });
  },
};
